/**
 * Window-registry quote channel (#1138).
 *
 * Each widget IIFE bundles its OWN copy of every shared module
 * (vite.config.ts `lib.formats: ['iife']`), so cross-widget state can never
 * live in JS module scope. This channel keeps the booking widget's quote
 * lifecycle on a versioned window registry (`window.__hrGuest.v1`) plus
 * CustomEvent notifications, so a separately-bundled consumer widget
 * (rate-comparison, split-cost, …) on the same page can render against it.
 *
 * Keying (#1148): every publisher is one MOUNT INSTANCE, registered under a
 * window-level sequence so two bundles can never collide; `bookingWidgetId`
 * is the grouping field consumers bind by. The same widget mounted twice on a
 * page (the layout slot picker allows it) is therefore two publishers with
 * one id — a visible `config-error` for auto-bound AND explicitly-bound
 * consumers, never a silent last-writer-wins on one snapshot. StrictMode's
 * mount/unmount/mount is a register/unregister/register sequence; nothing is
 * counted. The publisher registers under the feed its quote is fetched from
 * (`filter.feed_id`), the same feed consumers subscribe on.
 *
 * Liveness (#1149): a page script (SPA / page-builder swap) can remove a
 * booking host without React cleanup, leaving its registration behind. Each
 * instance carries its mount host WEAKLY; a publisher whose host is no longer
 * connected to the document is ignored on every read (`isConnected` is true
 * inside connected shadow roots). Disconnected entries are filtered, not
 * pruned — a page builder MOVING a live host detaches it for a moment, and
 * pruning on that read would kill a widget that is still alive. Only a host
 * the page has actually released (weak ref emptied by GC) is dropped, which a
 * move can never trigger. A read that observes a liveness flip announces it on
 * a microtask, so a consumer is never stranded on a state the DOM has left
 * behind. A detached tree's later publishes land only on its own dead entry.
 *
 * The snapshot names ONE canonical property identifier, `centralPropertyUuid`
 * (quotes internally carry a numeric `property_id`; the publisher resolves the
 * property's `uuid` field, then validates it, before publishing).
 *
 * Consumer binding: `subscribeToQuote` with no `bookingWidgetId` auto-binds
 * only while EXACTLY one live publisher exists for the feed. Zero publishers
 * is a `pending` state (never a permanent "missing publisher" — a consumer
 * may mount before the booking bundle registers); more than one is an
 * explicit `config-error` so a multi-booking page can never bind silently
 * wrong. Publisher registration/removal re-evaluates every subscriber.
 *
 * Mixed bundle versions on one page (a cached pre-#1148 booking bundle with
 * a current consumer, or the reverse) do not share this registry field, so
 * each side sees zero publishers and holds `pending` — hidden, never wrong.
 */

export type QuoteStatus = 'pending' | 'ready' | 'cleared' | 'error'

export type QuoteSnapshot = {
  status: QuoteStatus
  /**
   * Canonical Central property identifier (the property `uuid` field,
   * PROP-shaped today — see `central-uuid.ts`) — never the numeric quote
   * `property_id`, never an unvalidated token. Publishers validate before
   * naming it; null when the property fetch hasn't resolved or its
   * identifier fails validation.
   */
  centralPropertyUuid: string | null
  /** Selected rate plan's total, in `currency`. */
  total: number | null
  currency: string | null
  nights: number | null
  /** YYYY-MM-DD */
  checkin: string | null
  /** YYYY-MM-DD */
  checkout: string | null
  ratePlanId: string | null
  updatedAt: number
}

export type QuoteBinding =
  /** Bound to one publisher; `snapshot.status` carries the quote lifecycle. */
  | { kind: 'bound'; feedId: number; bookingWidgetId: string; snapshot: QuoteSnapshot }
  /** No publisher (yet) for this feed — hold a pending UI, never an error. */
  | { kind: 'pending'; feedId: number }
  /**
   * More than one live publisher matches (several booking widgets and no
   * explicit `bookingWidgetId`, or one id mounted twice — then the id
   * repeats) — the consumer must surface a visible configuration error,
   * never guess a binding.
   */
  | { kind: 'config-error'; feedId: number; publisherIds: string[] }

/** One mount instance of the booking publisher. */
export type QuotePublisher = {
  readonly instanceKey: string
  readonly feedId: number
  readonly bookingWidgetId: string
  publish(snapshot: Omit<QuoteSnapshot, 'updatedAt'>): void
  /** Dates were lost — the previous quote no longer stands. */
  clear(): void
  unregister(): void
}

/** Snapshot changed for `{feedId, bookingWidgetId}` (CustomEvent detail). */
export const QUOTE_CHANGED_EVENT = 'hr-quote-changed'
/** A publisher registered/unregistered for `{feedId, bookingWidgetId}`. */
export const QUOTE_PUBLISHER_CHANGED_EVENT = 'hr-quote-publisher-changed'

/**
 * How often a subscriber stuck in `config-error` looks again (see armRecheck).
 * Slow on purpose: the page is already showing an error banner, so this is a
 * recovery path, not a hot loop.
 */
export const CONFIG_ERROR_RECHECK_MS = 1000

/** A host reference that never keeps the element alive on its own. */
type HostRef = { deref(): Element | undefined }

type PublisherEntry = {
  feedId: number
  bookingWidgetId: string
  /**
   * Mount root the page can remove; null = no host, liveness opt-out.
   *
   * Held WEAKLY. A strong `Element` in a `window`-rooted Map made this channel
   * a retention root: every host a page script removed without React cleanup —
   * the exact #1149 case — pinned its detached subtree for the page's lifetime,
   * one per swap, unbounded (#1293 review measured 7 dead entries after 6
   * remount cycles). A WeakRef changes nothing about liveness reads (a MOVED
   * host is still strongly held by the page, so it stays deref-able) but lets
   * a genuinely abandoned one be collected, after which the entry is dropped.
   */
  hostRef: HostRef | null
  /** Last liveness reported, so a change is announced exactly once (see below). */
  wasLive: boolean
  snapshot: QuoteSnapshot
}

/** WeakRef where the runtime has it; a strong ref (previous behaviour) otherwise. */
function hostRefFor(host: Element | null): HostRef | null {
  if (host === null) return null
  const W = (globalThis as { WeakRef?: new (target: Element) => HostRef }).WeakRef
  return typeof W === 'function' ? new W(host) : { deref: () => host }
}

type GuestRegistryV1 = {
  publishers: Map<string, PublisherEntry>
  /** Window-level, so instance keys are unique across every bundle's copy. */
  publisherSeq: number
}

// SSR/no-window fallback so imports stay side-effect-safe anywhere. It is
// process-local (not shared cross-bundle) — real pages always have a window.
const fallbackRegistry: GuestRegistryV1 = {
  publishers: new Map(),
  publisherSeq: 0,
}

/**
 * Resolve (and lazily create) the shared v1 registry. Defensive per-field:
 * another IIFE bundle's copy of this module may have created `__hrGuest`
 * first, and a future v2 may share the root object.
 */
function registry(): GuestRegistryV1 {
  if (typeof window === 'undefined') return fallbackRegistry
  const w = window as unknown as {
    __hrGuest?: { v1?: Partial<GuestRegistryV1> }
  }
  const root = (w.__hrGuest = w.__hrGuest ?? {})
  const v1 = (root.v1 = root.v1 ?? {})
  if (!(v1.publishers instanceof Map)) v1.publishers = new Map()
  if (typeof v1.publisherSeq !== 'number') v1.publisherSeq = 0
  return v1 as GuestRegistryV1
}

function dispatch(event: string, feedId: number, bookingWidgetId: string) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(event, { detail: { feedId, bookingWidgetId } }))
}

function pendingSnapshot(): QuoteSnapshot {
  return {
    status: 'pending',
    centralPropertyUuid: null,
    total: null,
    currency: null,
    nights: null,
    checkin: null,
    checkout: null,
    ratePlanId: null,
    updatedAt: Date.now(),
  }
}

/**
 * Publisher (booking) side: register one mount instance. Seeds a `pending`
 * snapshot (a publisher exists before any dates are chosen). The returned
 * handle is the only way to publish for this instance; after `unregister`
 * every call on it is a no-op, so a torn-down tree's late effects can never
 * resurrect the entry.
 */
export function registerPublisher(
  feedId: number,
  bookingWidgetId: string,
  host: Element | null = null,
): QuotePublisher {
  const r = registry()
  const instanceKey = `${feedId}:${bookingWidgetId}:${++r.publisherSeq}`
  r.publishers.set(instanceKey, {
    feedId,
    bookingWidgetId,
    hostRef: hostRefFor(host),
    wasLive: true,
    snapshot: pendingSnapshot(),
  })
  dispatch(QUOTE_PUBLISHER_CHANGED_EVENT, feedId, bookingWidgetId)

  const publish = (snapshot: Omit<QuoteSnapshot, 'updatedAt'>) => {
    const entry = registry().publishers.get(instanceKey)
    if (!entry) return
    entry.snapshot = { ...snapshot, updatedAt: Date.now() }
    dispatch(QUOTE_CHANGED_EVENT, feedId, bookingWidgetId)
  }

  return {
    instanceKey,
    feedId,
    bookingWidgetId,
    publish,
    clear: () =>
      publish({
        status: 'cleared',
        centralPropertyUuid: null,
        total: null,
        currency: null,
        nights: null,
        checkin: null,
        checkout: null,
        ratePlanId: null,
      }),
    unregister: () => {
      if (!registry().publishers.delete(instanceKey)) return
      dispatch(QUOTE_PUBLISHER_CHANGED_EVENT, feedId, bookingWidgetId)
    },
  }
}

function isLive(entry: PublisherEntry): boolean {
  if (entry.hostRef === null) return true
  const host = entry.hostRef.deref()
  return host !== undefined && host.isConnected
}

/**
 * A publisher's host can die without any code running — a page script removes
 * it, React never cleans up, `unregisterPublisher` is never called — so nothing
 * dispatches and subscribers keep whatever they last computed. That left a
 * consumer stranded on a WRONG state: with two booking widgets and one host
 * removed, exactly one live publisher remained but the calculator went on
 * rendering "multiple booking widgets found on this page" forever (#1293
 * review). Reads already notice the death; they just never told anyone.
 *
 * So: whoever first observes a liveness FLIP announces it, once, on a
 * microtask. Async on purpose — a read happens inside a subscriber callback,
 * and dispatching there would re-enter the notify loop. `wasLive` lives on the
 * shared entry, so the flip is detected once across every bundle's copy, and it
 * is flipped BEFORE queueing, so the re-read the event triggers is a no-op and
 * this always converges.
 */
const pendingLivenessDispatch = new Set<string>()
let livenessFlushQueued = false

function queueLivenessDispatch(feedId: number, bookingWidgetId: string) {
  pendingLivenessDispatch.add(`${feedId}\u0000${bookingWidgetId}`)
  if (livenessFlushQueued) return
  livenessFlushQueued = true
  const flush = () => {
    livenessFlushQueued = false
    const keys = [...pendingLivenessDispatch]
    pendingLivenessDispatch.clear()
    for (const key of keys) {
      const sep = key.indexOf('\u0000')
      dispatch(QUOTE_PUBLISHER_CHANGED_EVENT, Number(key.slice(0, sep)), key.slice(sep + 1))
    }
  }
  if (typeof queueMicrotask === 'function') queueMicrotask(flush)
  else setTimeout(flush, 0)
}

/**
 * Live publishers for a feed, optionally narrowed to one widget id, in
 * registration order. Also the channel's only housekeeping pass: an entry whose
 * host has been GARBAGE-COLLECTED is dropped (the page released its last
 * reference, so it can never come back), and any liveness flip is announced.
 * A merely detached host is still deref-able, so a page builder MOVING a live
 * host is untouched — the reason this never prunes on `isConnected` alone.
 */
function livePublishers(feedId: number, bookingWidgetId: string | undefined): PublisherEntry[] {
  const live: PublisherEntry[] = []
  const publishers = registry().publishers
  for (const [instanceKey, entry] of publishers) {
    if (entry.hostRef !== null && entry.hostRef.deref() === undefined) {
      publishers.delete(instanceKey)
      continue
    }
    const alive = isLive(entry)
    if (alive !== entry.wasLive) {
      entry.wasLive = alive
      queueLivenessDispatch(entry.feedId, entry.bookingWidgetId)
    }
    if (entry.feedId !== feedId) continue
    if (bookingWidgetId !== undefined && entry.bookingWidgetId !== bookingWidgetId) continue
    if (alive) live.push(entry)
  }
  return live
}

/**
 * Resolve the current binding for a consumer. Exported for imperative reads
 * and tests; `subscribeToQuote` is the subscription form of the same rules.
 * An explicit `bookingWidgetId` narrows to that id (a consumer may mount
 * before its publisher: no match is `pending`, not an error); an empty one
 * auto-binds across the feed.
 */
export function getQuoteBinding(
  feedId: number,
  bookingWidgetId: string | undefined,
): QuoteBinding {
  const live = livePublishers(feedId, bookingWidgetId || undefined)
  if (live.length === 0) return { kind: 'pending', feedId }
  if (live.length > 1) {
    return { kind: 'config-error', feedId, publisherIds: live.map((e) => e.bookingWidgetId) }
  }
  const [entry] = live
  return { kind: 'bound', feedId, bookingWidgetId: entry.bookingWidgetId, snapshot: entry.snapshot }
}

/**
 * Consumer side. Replays the current binding SYNCHRONOUSLY on subscribe (a
 * consumer mounted after the quote is `ready` sees `ready` immediately), then
 * re-notifies on every snapshot change and on every publisher add/remove (so
 * an auto-bound consumer re-evaluates its binding, per R4#2). Returns an
 * unsubscribe function.
 */
export function subscribeToQuote(
  feedId: number,
  bookingWidgetId: string | undefined,
  cb: (binding: QuoteBinding) => void,
): () => void {
  let lastKind: QuoteBinding['kind'] | null = null
  let recheckTimer: ReturnType<typeof setTimeout> | null = null
  const clearRecheck = () => {
    if (recheckTimer === null) return
    clearTimeout(recheckTimer)
    recheckTimer = null
  }
  /**
   * The liveness flip above is announced by whoever READS — and in the one
   * state that can be actively WRONG, nobody does. A page script removing one
   * of two booking hosts fires nothing, so a consumer sat on `config-error`
   * has no reason to look again and keeps telling the guest "multiple booking
   * widgets found on this page" when only one is left (#1293 review).
   *
   * So while (and only while) this subscriber is in `config-error`, re-read on
   * a slow timer. Deliberately NOT general polling — the PR's reasoning against
   * that stands. This is scoped to a state that is already a visible
   * misconfiguration, it cancels itself the moment the binding resolves or the
   * consumer unsubscribes, and the re-read also drives the shared flip
   * announcement, so every other subscriber recovers with it.
   */
  const armRecheck = () => {
    clearRecheck()
    if (lastKind !== 'config-error') return
    recheckTimer = setTimeout(() => {
      recheckTimer = null
      const next = getQuoteBinding(feedId, bookingWidgetId)
      if (next.kind !== lastKind) notify()
      else armRecheck()
    }, CONFIG_ERROR_RECHECK_MS)
  }
  const notify = () => {
    const binding = getQuoteBinding(feedId, bookingWidgetId)
    lastKind = binding.kind
    cb(binding)
    armRecheck()
  }
  const relevant = (e: Event): boolean => {
    const detail = (e as CustomEvent<{ feedId?: number; bookingWidgetId?: string }>).detail
    if (!detail || detail.feedId !== feedId) return false
    // Explicitly bound: only the named publisher's changes matter. Auto-bound:
    // any publisher in the feed can change the binding itself.
    return !bookingWidgetId || detail.bookingWidgetId === bookingWidgetId
  }
  const onQuoteChanged = (e: Event) => {
    if (relevant(e)) notify()
  }
  const onPublisherChanged = (e: Event) => {
    if (relevant(e)) notify()
  }
  // Replay BEFORE attaching listeners: if the callback throws on the
  // synchronous replay, no listeners have been added yet — otherwise both
  // would leak for the page lifetime (the unsubscribe fn is never returned).
  notify()
  if (typeof window !== 'undefined') {
    window.addEventListener(QUOTE_CHANGED_EVENT, onQuoteChanged)
    window.addEventListener(QUOTE_PUBLISHER_CHANGED_EVENT, onPublisherChanged)
  }
  return () => {
    clearRecheck()
    if (typeof window === 'undefined') return
    window.removeEventListener(QUOTE_CHANGED_EVENT, onQuoteChanged)
    window.removeEventListener(QUOTE_PUBLISHER_CHANGED_EVENT, onPublisherChanged)
  }
}
